import { Children, Fragment, isValidElement, useMemo } from "react";
import { View } from "react-native";
import { isNumber } from "lodash-es";

import type {
  FlexStyle,
  LayoutChangeEvent,
  AccessibilityProps,
  StyleProp,
  ViewProps,
  ViewStyle,
} from "react-native";
import type { ReactNode } from "react";
import type { TestProps } from "../../types";

export interface StackProps
  extends TestProps,
    AccessibilityProps,
    Pick<ViewProps, "pointerEvents"> {
  width?: number | string;
  spacing?: number | string; // e.g. margin values
  align?: "start" | "end" | "center" | "stretch";
  justify?: "start" | "center" | "end" | "space-between" | "space-around";
  wrap?: boolean;
  children: ReactNode;
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  style?: StyleProp<ViewStyle>;
  onLayout?: ((event: LayoutChangeEvent) => void) | undefined;
  flexChildren?: boolean;
}

export interface DirectionStackProps extends StackProps {
  reverse?: boolean;
}

/**
 * HStack / VStack – Layout components for horizontally or vertically spacing all children equidistant.
 * Frequently used with spacing={spacing.md}.
 *
 * example: https://smartrent-ui.com/components/layout/stack
 */
export function Stack({
  children,
  align,
  justify,
  direction,
  wrap,
  spacing = 0,
  width,
  style,
  flexChildren = false,
  onLayout,
  testID,
  ...props
}: StackProps) {
  const alignItems = useMemo<FlexStyle["alignItems"]>(() => {
    switch (align) {
      case "start":
        return "flex-start";
      case "center":
        return "center";
      case "end":
        return "flex-end";
      default:
        return "stretch";
    }
  }, [align]);

  const justifyContent = useMemo<FlexStyle["justifyContent"]>(() => {
    switch (justify) {
      case "start":
        return "flex-start";
      case "end":
        return "flex-end";
      case "space-between":
        return "space-between";
      case "space-around":
        return "space-around";
      default:
        return "flex-start";
    }
  }, [justify]);

  const computedStyle = useMemo<ViewStyle>(() => {
    return {
      width,
      flexDirection: direction,
      alignItems,
      justifyContent,
      flexWrap: wrap ? "wrap" : undefined,
      gap: isNumber(spacing) ? spacing : undefined,
    };
  }, [width, direction, alignItems, justifyContent, wrap, spacing]);

  const flattenedChildren = useMemo(() => {
    return Children.toArray(children)
      .filter(isValidElement)
      .reduce((acc: any[], child: any) => {
        if (child.type === Symbol.for("react.fragment")) {
          if (Array.isArray(child.props.children)) {
            return [...acc, ...child.props.children];
          } else {
            return [...acc, child.props.children];
          }
        }

        return [...acc, child];
      }, []);
  }, [children]);

  return (
    <View
      style={[computedStyle, style]}
      testID={testID}
      onLayout={onLayout}
      {...props}
    >
      {flattenedChildren.map((child, idx) => {
        if (flexChildren) {
          return (
            <View key={idx} style={{ flex: 1 }}>
              {child}
            </View>
          );
        }

        return <Fragment key={idx}>{child}</Fragment>;
      })}
    </View>
  );
}
